import { useEffect, useState } from 'react';

import {
  CoOwner,
  Encumbrance,
  Range,
  ScriptureData,
  SelectorOption,
} from 'types';

import { removeRangeFromRanges } from 'utils/removeRangeFromRanges';
import { isValidRange } from 'utils/isValidRange';
import { SingleValue } from 'react-select';
import { FixedShare } from '../types/Share';
import getInitialRanges from '../utils/getInitialRanges';
import getValidRanges from '../utils/getValidRanges';
import getScriptureDocument from '../utils/getScriptureDocument';
import { EncumbranceTypes } from '../constants/encumbranceOptions';

type Props = {
  share: FixedShare;
};

export default function useEncumbranceForm({ share }: Props) {
  const [affectedShares, setAffectedShares] = useState<number>(
    (share?.to || 0) - (share?.from || 0) + 1
  );
  const [encumbranceName, setEncumbranceName] = useState<
    EncumbranceTypes | string
  >('');
  const [encumbranceNameOption, setEncumbranceNameOption] =
    useState<SelectorOption | null>(null);
  const [encumbranceDate, setEncumbranceDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [encumbranceFrom, setEncumbranceFrom] = useState<number>(share?.from);
  const [encumbranceTo, setEncumbranceTo] = useState<number>(share?.to);
  const [encumbranceScriptureData, setEncumbranceScriptureData] =
    useState<ScriptureData>({ notaryName: '', protocolNr: '', notaryCity: '' });
  const [encumbranceFile, setEncumbranceFile] = useState<File | undefined>();

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const [editingIndex, setEditingIndex] = useState<number>(-1);

  const [rangeIsValid, setRangeIsValid] = useState<boolean>(true);

  const [validRanges, setValidRanges] = useState<Range[]>([
    { from: share?.from, to: share?.to },
  ]);

  const [coOwners, setCoOwners] = useState<CoOwner[]>([]);

  const [inputLabel, setInputLabel] = useState<string>('DragDocumentToUpload');

  const setValidRangeOnInput = (ranges: Range[]) => {
    const range = ranges.find(
      (range) => (range?.to || 0) - (range?.from || 0) + 1 > 0
    );
    if (range) {
      setEncumbranceFrom(range?.from);
      setEncumbranceTo(range?.to);
      setAffectedShares(range?.to - range?.from + 1);
    }
    setRangeIsValid(true);
  };

  const setEncumbranceValues = (encumbrance: Encumbrance) => {
    setEncumbranceName(encumbrance?.name);
    setEncumbranceDate(encumbrance?.date);
    setEncumbranceFrom(encumbrance?.from);
    setEncumbranceTo(encumbrance?.to);
    setEncumbranceScriptureData(encumbrance?.scriptureData);
    setCoOwners(encumbrance?.coOwners);
  };

  const setEncumbranceDocumentValues = async (encumbrance: Encumbrance) => {
    if (encumbrance.file) {
      setEncumbranceFile(encumbrance.file);
    }

    if (encumbrance?.scriptureDocument) {
      const document = await getScriptureDocument(
        encumbrance.scriptureDocument
      );
      setInputLabel(document?.name || 'DragDocumentToUpload');
    } else {
      setInputLabel('DragDocumentToUpload');
    }
  };

  const setEditValues = (index: number, encumbrances: Encumbrance[]) => {
    const encumbrance = encumbrances[index];
    setEncumbranceValues(encumbrance);

    setEncumbranceDocumentValues(encumbrance);

    setIsEditing(true);
    setEditingIndex(index);

    const ranges = getValidRanges(encumbrance, validRanges);
    setValidRanges(ranges);
    setRangeIsValid(true);
  };

  const handleChangeEncumbranceName = (option: SingleValue<SelectorOption>) => {
    if (option) {
      setEncumbranceName(option.value);
      setEncumbranceNameOption(option);
    }
  };

  const handleChangeEncumbranceDate = (event: any) => {
    setEncumbranceDate(event.target.value);
  };

  const handleChangeEncumbranceFrom = (event: any) => {
    const { value } = event.target;

    setEncumbranceFrom(+value);

    const isValid = isValidRange({
      from: +value,
      to: encumbranceTo,
      validRanges,
    });
    setRangeIsValid(isValid);

    if (encumbranceTo) {
      setAffectedShares(encumbranceTo - value + 1);
    }
  };

  const handleChangeEncumbranceTo = (event: any) => {
    const { value } = event.target;

    setEncumbranceTo(+value);

    const isValid = isValidRange({
      from: encumbranceFrom,
      to: +value,
      validRanges,
    });
    setRangeIsValid(isValid);

    if (encumbranceFrom) {
      setAffectedShares(value - encumbranceFrom + 1);
    }
  };

  const handleChangeNotaryName = (event: any) => {
    setEncumbranceScriptureData({
      ...encumbranceScriptureData,
      notaryName: event.target.value,
    });
  };

  const handleChangeProtocolNr = (event: any) => {
    setEncumbranceScriptureData({
      ...encumbranceScriptureData,
      protocolNr: event.target.value,
    });
  };

  const handleChangeNotaryCity = (event: any) => {
    setEncumbranceScriptureData({
      ...encumbranceScriptureData,
      notaryCity: event.target.value,
    });
  };

  const handleAddCoOwner = (coOwner: CoOwner) => {
    setCoOwners([...coOwners, coOwner]);
  };

  const handleRemoveCoOwner = (index: number) => {
    const filteredCoOwners = coOwners.filter((_, i) => i !== index);
    setCoOwners(filteredCoOwners);
  };

  const handleChangeEncumbranceFile = (file: File) => {
    setEncumbranceFile(file);
  };

  const handleClickRange = (range: Range) => {
    setEncumbranceFrom(range?.from);
    setEncumbranceTo(range?.to);
    setAffectedShares(range?.to - range?.from + 1);
    setRangeIsValid(true);
  };

  const handleCleanForm = () => {
    setEncumbranceName('');
    setEncumbranceDate('');
    setEncumbranceScriptureData({
      notaryName: '',
      protocolNr: '',
      notaryCity: '',
    });
    setEncumbranceFile(undefined);
    setCoOwners([]);
    setInputLabel('DragDocumentToUpload');
  };

  const handleCancelEdition = (encumbrances: Encumbrance[]) => {
    const encumbrance = encumbrances[editingIndex];
    const ranges = removeRangeFromRanges(
      { from: encumbrance?.from, to: encumbrance?.to },
      validRanges
    );
    setValidRanges(ranges);
    setValidRangeOnInput(ranges);
    setIsEditing(false);
    handleCleanForm();
  };

  useEffect(() => {
    if (share?.encumbrances?.length > 0) {
      const ranges = getInitialRanges(share);
      setValidRanges(ranges);
      setValidRangeOnInput(ranges);
    }
  }, [share]);

  useEffect(() => {
    if (encumbranceFrom && encumbranceTo && encumbranceFrom <= encumbranceTo) {
      setAffectedShares(encumbranceTo - encumbranceFrom + 1);
    }
  }, [encumbranceFrom, encumbranceTo]);

  return {
    data: {
      affectedShares,
      encumbranceName,
      encumbranceNameOption,
      encumbranceDate,
      encumbranceFrom,
      encumbranceTo,
      encumbranceScriptureData,
      encumbranceFile,
      validRanges,
      isEditing,
      editingIndex,
      rangeIsValid,
      coOwners,
      inputLabel,
    },
    isLoading: false,
    isError: false,
    actions: {
      handleChangeEncumbranceName,
      handleChangeEncumbranceDate,
      handleChangeEncumbranceFrom,
      handleChangeEncumbranceTo,
      handleChangeNotaryName,
      handleChangeProtocolNr,
      handleChangeNotaryCity,
      handleAddCoOwner,
      handleRemoveCoOwner,
      handleChangeEncumbranceFile,
      handleClickRange,
      handleCleanForm,
      handleCancelEdition,
      setCoOwners,
      setIsEditing,
      setValidRanges,
      setValidRangeOnInput,
      setEditValues,
    },
  };
}
